import { FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import LeftArrow from "assets/icons/ArrowLeftHovered.svg";
import RedLoop from "assets/svg/SearchIconRed.svg";
import { TextType } from "components/Atoms/CustomText/CustomTextModel";
import { COLORS } from "constants/variables";
import { appRoutes } from "config/AppRoutesConfig";
import InputAtom from "components/Atoms/InputAtom/InputAtom";
import CustomText from "components/Atoms/CustomText/CustomText";
import EventCard from "components/EventCard/EventCard";
import Pagination from "components/Atoms/Paginations/Pagination";
import HeaderTab from "components/HeaderTab/HeaderTab";
import newsAndMediaService from "services/newsAndMediaService";
import { EventCalendarQuery } from "services/eventCalendarQuery";
import { accessibilityBiggerCursorSelector } from "slices/UISlice/uiSliceSelectors";
import EventPeriodFilter from "./EventPeriodFilter";
import { SelectButton } from "./EventPeriodFilterStyled";
import {
  OfficialAnnouncementsContainer, BackToWrapper, BackToImage, BackToButton,
  InputContainer, ImgStyled, Wrapper, CardContainer, TitleWrapper, HeaderWrapper,
} from "./EventsStyled";

const Events = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const biggerCursor = useSelector(accessibilityBiggerCursorSelector);
  const topRef = useRef<HTMLDivElement>(null);
  const [inputText, setInputText] = useState("");
  const [query, setQuery] = useState<EventCalendarQuery>(() => {
    const now = new Date();
    return {
      page: 1, pageSize: 6, search: "", status: "upcoming", eventType: "all",
      period: { scope: "all", month: now.getMonth(), year: now.getFullYear() },
    };
  });
  const [events, setEvents] = useState<any>();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const totalPages = events?.meta?.pagination?.pageCount ?? 0;

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setHasError(false);
    newsAndMediaService.getCalendarEvents(query, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setEvents(data);
        setIsLoading(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setHasError(true);
        setIsLoading(false);
      });
    return () => controller.abort();
  }, [query]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setQuery((previous) => ({ ...previous, search: inputText.trim(), page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setQuery((previous) => ({ ...previous, page }));
    topRef.current?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      block: "start",
    });
  };

  const tabs = [
    { id: 0, tabTitle: t("view.newsAndMedia.events.currentEvents"), content: null },
    { id: 1, tabTitle: t("view.newsAndMedia.events.pastEvents"), content: null },
  ];

  return (
    <OfficialAnnouncementsContainer ref={topRef}>
      <TitleWrapper>
        <BackToWrapper onClick={() => navigate(appRoutes.news)} biggerCursor={biggerCursor}>
          <BackToImage src={LeftArrow} alt="backIcon" />
          <BackToButton biggerCursor={biggerCursor}>
            <CustomText text={t("view.newsAndMedia.officialAnnouncements.backButton")} type={TextType.H5bold} color={COLORS.RaisinBlack} />
          </BackToButton>
        </BackToWrapper>
        <CustomText text={t("view.newsAndMedia.events.filters.eventsCalendar")} type={TextType.H2} />
      </TitleWrapper>
      <HeaderWrapper>
        <HeaderTab tabs={tabs}
          onTabSelect={(id) => setQuery((previous) => ({ ...previous, status: id === 1 ? "past" : "upcoming", page: 1 }))}
          filtersContent={
            <EventPeriodFilter value={query.period} eventType={query.eventType}
              onChange={(period) => setQuery((previous) => ({ ...previous, period, page: 1 }))}
              onEventTypeChange={(eventType) => setQuery((previous) => ({ ...previous, eventType, page: 1 }))} />
          }
        />
      </HeaderWrapper>
      <InputContainer>
        <form role="search" onSubmit={handleSearch}>
          <InputAtom startAdornment={<ImgStyled src={RedLoop} alt="" />}
            endAdornment={<SelectButton type="submit">{t("view.newsAndMedia.officialAnnouncements.search")}</SelectButton>}
            placeholder={t("view.newsAndMedia.officialAnnouncements.searchPlaceholder")}
            isSearchBar value={inputText}
            onChange={(event) => {
              const value = event.target.value;
              setInputText(value);
              if (!value.trim()) setQuery((previous) => ({ ...previous, search: "", page: 1 }));
            }} />
        </form>
      </InputContainer>
      <Wrapper>
        {isLoading || hasError ? (
          <div role={hasError ? "alert" : "status"} aria-live="polite">
            <CustomText text={t(hasError ? "view.newsAndMedia.events.loadError" : "common.loading")} type={TextType.P1} color={COLORS.RaisinBlack} />
            {hasError && <SelectButton type="button" onClick={() => setQuery((previous) => ({ ...previous }))}>{t("view.newsAndMedia.events.retry")}</SelectButton>}
          </div>
        ) : events?.data?.length > 0 ? (
          <CardContainer>{events.data.map((card) => <EventCard key={card.id} {...card} />)}</CardContainer>
        ) : (
          <div role="status"><CustomText text={t("app.noEvents")} type={TextType.P1} color={COLORS.MetalicSilver} /></div>
        )}
      </Wrapper>
      {!isLoading && !hasError && totalPages > 1 && (
        <Pagination totalPages={totalPages} currentPage={query.page} onPageChange={handlePageChange} />
      )}
    </OfficialAnnouncementsContainer>
  );
};

export default Events;
